/**
 *
 * App
 *
 * This component is the skeleton around the actual pages, and should only
 * contain code that should be seen on all pages. (e.g. navigation bar)
 */

import {
  combineThemeDescription,
  generateTheme,
  ThemeProvider,
} from "@envestnet/envreact-component-library";
import React, { useMemo } from "react";
import { Helmet } from "react-helmet";
import { Route, Router } from "react-router-dom";
import { AuthProvider, useAuth } from "react-oidc-context";
import { User } from "oidc-client-ts";

import AppAuthenticators from "scenes/AppAuthenticators";
import ChallengeQuestion from "scenes/ChallengeQuestion";
import Dashboard from "scenes/Dashboard";
import EnvAuthenticator from "scenes/EnvAuthenticator";
import NotFoundPage from "scenes/NotFoundPage";
import OktaVerification from "scenes/OktaVerification";
import PasswordSetup from "scenes/PasswordSetupPage";
import PhoneAuthenticator from "scenes/PhoneAuthenticator";
import SecuritySetUp from "scenes/SecuritySetUp";
import Loading from "scenes/Loading";
import PlatformTransfer from "scenes/PlatformTransfer";
import { AppError } from "components/AppError";
import { SignoutPage } from "components/SignoutPage";
import storage from "../../persistance";
import {
  getPlatformTransferPath,
  isPlatformTransferPath,
  PLATFORM_TRANSFER_PATH,
} from "utils/platformTransfer";
import { ThemeMode, ThemeModeProvider } from "./themeModeContext";
import EmailAuthenticator from "scenes/EmailAuthenticator";

const THEME_MODE_STORAGE_KEY = "themeMode";

const getInitialThemeMode = (): ThemeMode => {
  const storedThemeMode = storage.getItem(THEME_MODE_STORAGE_KEY);

  if (storedThemeMode === "light" || storedThemeMode === "dark") {
    return storedThemeMode;
  }

  return window.matchMedia("(prefers-color-scheme: dark)").matches
    ? "dark"
    : "light";
};

export default function App({ history }) {
  const { ISSUER, REDIRECT_URI, CLIENT_ID, SCOPES } = window?.config || {
    ISSUER: "",
    CLIENT_ID: "",
    REDIRECT_URI: "",
    SCOPES: ["offline_access", "comsvc/loginspa", "openid", "profile", "email"],
  };

  const [themeMode, setThemeMode] =
    React.useState<ThemeMode>(getInitialThemeMode);
  const [pathname, setPathname] = React.useState(history.location.pathname);

  const isDashboardFlow =
    pathname.includes("/dashboard") || pathname === "/signedOut";
  const activeThemeMode: ThemeMode = isDashboardFlow ? themeMode : "light";

  React.useEffect(() => {
    const unlisten = history.listen((location) => {
      setPathname(location.pathname);
    });

    return () => unlisten();
  }, [history]);

  React.useEffect(() => {
    // When passkey registration is cancelled, Keycloak redirects back with error params.
    // If this route is opened in a popup, close it instead of rendering the full app there.
    if (
      window.name !== "passkey-setup" ||
      window.location.pathname !== "/dashboard/securitysettings"
    ) {
      return;
    }

    const params = new URLSearchParams(window.location.search);
    const error = params.get("error");
    const actionStatus = params.get("kc_action_status");
    const code = params.get("code");

    if (
      error ||
      actionStatus === "cancelled" ||
      actionStatus === "success" ||
      !!code
    ) {
      window.close();
    }
  }, []);

  const toggleThemeMode = React.useCallback(() => {
    if (pathname !== "/dashboard" && !pathname.includes("/dashboard/")) {
      return;
    }

    setThemeMode((currentThemeMode) =>
      currentThemeMode === "light" ? "dark" : "light"
    );
  }, [pathname]);

  React.useEffect(() => {
    if (isDashboardFlow) {
      storage.setItem(THEME_MODE_STORAGE_KEY, themeMode);
    }
  }, [isDashboardFlow, themeMode]);

  const theme = useMemo(
    () => generateTheme(combineThemeDescription(), activeThemeMode),
    [activeThemeMode]
  );

  const oidcConfig: any = {
    authority: ISSUER,
    client_id: CLIENT_ID,
    redirectUri: window.location.origin + REDIRECT_URI,
    post_logout_redirect_uri: `${window.location.origin}/signedOut`,
    automaticSilentRenew: false,
    monitorSession: true,
    scope: SCOPES.join(" "),
    refreshTokenAllowedScope: SCOPES.join(" "),
    metadata: {
      authorization_endpoint: `${window.config.API_URL}/oauth2/v2/authorize`,
      revocation_endpoint: `${window.config.API_URL}/oauth2/v2/revoke`,
      token_endpoint: `${window.config.API_URL}/oauth2/v2/token`,
      end_session_endpoint: `${window.config.API_URL}/oauth2/v2/logout`,
    },
  };

  oidcConfig.onSigninCallback = (user: User) => {
    // Closes the new window after passkey registration.
    if (window.name === "passkey-setup") {
      window.close();
      return;
    }

    if (user?.state && user?.state !== "empty") {
      storage.setItem("resourceId", user.state as string);
    }

    window.IdentitySession.init(
      {
        accessToken: user.access_token,
        refreshToken: user.refresh_token,
      },
      {
        tokenUrl: `${window.config.API_URL}/oauth2/v2/token`,
        clientId: window.config.CLIENT_ID,
        keepaliveUrl: `${window.config.API_URL}/ciam/ext/session/keepalive`,
      },
      {
        onSessionExpired: () => (window.location.href = "/signedOut"),
      }
    );

    document.addEventListener("click", window.IdentitySession.keepalive);
    document.addEventListener("keydown", window.IdentitySession.keepalive);

    // The platform transfer keeps its own path so a reload reloads the same scene.
    window.history.replaceState(
      {},
      document.title,
      isPlatformTransferPath()
        ? window.location.origin + getPlatformTransferPath()
        : window.location.origin
    );
  };

  oidcConfig.onSignoutCallback = () => {
    storage.removeItem("resourceId");
    storage.removeItem(THEME_MODE_STORAGE_KEY);
  };

  // Custom silent renew with explicit scopes
  const CustomSilentRenew = () => {
    const auth = useAuth();

    React.useEffect(() => {
      if (!auth.user) return undefined;

      const handleSilentRenew = () => {
        auth.signinSilent({
          scope: SCOPES.join(" "),
          extraTokenParams: { scope: SCOPES.join(" ") },
        });
      };

      auth.events.addAccessTokenExpiring(handleSilentRenew);

      return () => auth.events.removeAccessTokenExpiring(handleSilentRenew);
    }, [auth]);

    React.useEffect(() => {
      const handleUserUnloaded = () => {
        storage.removeItem(THEME_MODE_STORAGE_KEY);
      };

      auth.events.addUserUnloaded(handleUserUnloaded);

      return () => auth.events.removeUserUnloaded(handleUserUnloaded);
    }, [auth]);

    return null;
  };

  return (
    <ThemeModeProvider value={{ themeMode: activeThemeMode, toggleThemeMode }}>
      <ThemeProvider theme={theme}>
        <Helmet defaultTitle="APP">
          <meta name="description" content="WebApp by Envestnet" />
        </Helmet>
        <AuthProvider {...oidcConfig}>
          <CustomSilentRenew />
          <Router history={history}>
            <Route path="/" component={Loading} exact />
            <Route path="/notfound" component={NotFoundPage} />
            <Route path="/activate" component={ChallengeQuestion} />
            <Route path="/security" component={SecuritySetUp} />
            <Route path="/okta-verify" component={OktaVerification} />
            <Route path="/app-verify" component={AppAuthenticators} />
            <Route path="/email-otp" component={EmailAuthenticator} />

            <Route path="/env-auth-verify" component={EnvAuthenticator} />
            <Route path="/phone" component={PhoneAuthenticator} />
            <Route path="/password-setup" component={PasswordSetup} />
            <Route path="/dashboard" component={Dashboard} />
            <Route path={PLATFORM_TRANSFER_PATH} component={PlatformTransfer} />
            <Route path="/ssoError" component={AppError} exact />
            <Route path="/signedOut" component={SignoutPage} exact />
          </Router>
        </AuthProvider>
      </ThemeProvider>
    </ThemeModeProvider>
  );
}
